/**
 * <s-ring-stage> — interactive 3D ring of product lines on the homepage.
 * Drag (mouse or finger) spins the ring with inertia and settles on the nearest card;
 * prev/next buttons and arrow keys step one card; clicking a side card brings it to the front;
 * line-name tabs ([data-ring-goto]) jump straight to a line; the front card's status and link show in the caption and its tab is marked aria-current. When idle the ring drifts slowly.
 * The pointer also tilts the ring (--tx/--ty) for depth. Reduced motion: no drift, no tilt, instant steps.
 */
class SRingStage extends HTMLElement {
  #abort;
  #frame = 0;
  #rot = 0;
  #vel = 0;
  #target = null;
  #drag = null;
  #suppressClick = false;
  #idleUntil = 0;
  #hover = false;
  #visible = true;
  #active = -1;
  #tilt = { x: 0, y: 0, tx: 0, ty: 0 };

  connectedCallback() {
    this.ring = this.querySelector('.ring');
    this.viewport = this.querySelector('.ring-stage');
    if (!this.ring || !this.viewport) return;
    this.cards = [...this.ring.querySelectorAll('.ring-card')];
    this.n = this.cards.length;
    if (this.n < 2) return;
    this.step = 360 / this.n;
    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.fine = window.matchMedia('(pointer: fine)').matches;
    this.caption = {
      name: this.querySelector('[data-ring-name]'),
      status: this.querySelector('[data-ring-status]'),
      link: this.querySelector('[data-ring-link]'),
      tabs: this.querySelector('[data-ring-tabs]'),
      tabButtons: [...this.querySelectorAll('[data-ring-goto]')],
    };

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const vp = this.viewport;

    this.classList.add('is-interactive');
    this.#apply();

    vp.addEventListener('pointerdown', (e) => this.#onDown(e), { signal });
    vp.addEventListener('pointermove', (e) => this.#onMove(e), { signal });
    vp.addEventListener('pointerup', (e) => this.#onUp(e), { signal });
    vp.addEventListener('pointercancel', (e) => this.#onUp(e), { signal });
    vp.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') this.#hover = true; }, { signal });
    vp.addEventListener('pointerleave', () => { this.#hover = false; }, { signal });
    vp.addEventListener('click', (e) => this.#onClick(e), { capture: true, signal });
    vp.addEventListener('dragstart', (e) => e.preventDefault(), { signal });

    this.querySelector('[data-ring-prev]')?.addEventListener('click', () => this.#goTo(this.#active - 1), { signal });
    this.querySelector('[data-ring-next]')?.addEventListener('click', () => this.#goTo(this.#active + 1), { signal });
    this.caption.tabButtons.forEach((tab) => tab.addEventListener('click', () => this.#goTo(Number(tab.dataset.ringGoto)), { signal }));
    this.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      const forward = (e.key === 'ArrowRight') !== rtl;
      e.preventDefault();
      this.#goTo(this.#active + (forward ? 1 : -1));
    }, { signal });

    if (this.fine && !this.reduce) {
      this.addEventListener('pointermove', (e) => {
        const box = this.getBoundingClientRect();
        this.#tilt.tx = ((e.clientX - box.left) / box.width) * 2 - 1;
        this.#tilt.ty = ((e.clientY - box.top) / box.height) * 2 - 1;
        this.#kick();
      }, { signal });
      this.addEventListener('pointerleave', () => { this.#tilt.tx = 0; this.#tilt.ty = 0; this.#kick(); }, { signal });
    }

    const io = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      if (this.#visible) this.#kick();
    });
    io.observe(this);
    signal.addEventListener('abort', () => io.disconnect());
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.#kick(); }, { signal });

    this.#kick();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #onDown(e) {
    if (e.button !== 0) return;
    this.#drag = { id: e.pointerId, startX: e.clientX, lastX: e.clientX, lastT: performance.now(), moved: false };
    this.#vel = 0;
    this.#target = null;
    this.#suppressClick = false;
    this.#kick();
  }

  #onMove(e) {
    const d = this.#drag;
    if (!d || d.id !== e.pointerId) return;
    if (!d.moved && Math.abs(e.clientX - d.startX) > 6) {
      d.moved = true;
      this.viewport.setPointerCapture(e.pointerId);
      this.classList.add('is-dragging');
    }
    if (!d.moved) return;
    const now = performance.now();
    const k = 300 / Math.max(this.viewport.clientWidth, 320);
    const delta = (e.clientX - d.lastX) * k;
    this.#rot += delta;
    const dt = Math.max(now - d.lastT, 1);
    this.#vel = (delta / dt) * 16;
    d.lastX = e.clientX;
    d.lastT = now;
  }

  #onUp(e) {
    const d = this.#drag;
    if (!d || d.id !== e.pointerId) return;
    if (d.moved) {
      this.#suppressClick = true;
      if (performance.now() - d.lastT > 80) this.#vel = 0;
      if (this.reduce) { this.#vel = 0; this.#rot = this.#snap(this.#rot); }
    }
    this.#drag = null;
    this.classList.remove('is-dragging');
    this.#idleUntil = performance.now() + 5000;
    this.#kick();
  }

  #onClick(e) {
    if (this.#suppressClick) {
      e.preventDefault();
      e.stopPropagation();
      this.#suppressClick = false;
      return;
    }
    const card = e.target.closest('.ring-card');
    if (!card) return;
    const index = this.cards.indexOf(card);
    if (index !== this.#active) {
      e.preventDefault();
      this.#goTo(index);
    }
  }

  #snap(r) {
    return Math.round(r / this.step) * this.step;
  }

  #indexAt(r) {
    const i = Math.round(-r / this.step) % this.n;
    return i < 0 ? i + this.n : i;
  }

  #goTo(index) {
    const wanted = ((index % this.n) + this.n) % this.n;
    let diff = (wanted - this.#indexAt(this.#rot) + this.n) % this.n;
    if (diff > this.n / 2) diff -= this.n;
    const goal = this.#snap(this.#rot) - diff * this.step;
    this.#vel = 0;
    this.#idleUntil = performance.now() + 7000;
    if (this.reduce) {
      this.#rot = goal;
      this.#target = null;
      this.#apply();
    } else {
      this.#target = goal;
      this.#kick();
    }
  }

  #kick() {
    if (!this.#frame) this.#frame = requestAnimationFrame(() => this.#tick());
  }

  #tick() {
    this.#frame = 0;
    if (!this.#visible || document.hidden) return;
    let busy = false;

    if (this.#drag?.moved) {
      busy = true;
    } else if (Math.abs(this.#vel) > 0.04) {
      this.#rot += this.#vel;
      this.#vel *= 0.94;
      if (Math.abs(this.#vel) <= 0.04) { this.#vel = 0; this.#target = this.#snap(this.#rot); }
      busy = true;
    } else if (this.#target !== null) {
      this.#rot += (this.#target - this.#rot) * 0.12;
      if (Math.abs(this.#target - this.#rot) < 0.05) { this.#rot = this.#target; this.#target = null; }
      busy = true;
    } else if (!this.reduce && !this.#hover && !this.#drag && performance.now() > this.#idleUntil) {
      this.#rot -= 0.06;
      busy = true;
    } else if (!this.reduce) {
      busy = true;
    }

    const t = this.#tilt;
    t.x += (t.tx - t.x) * 0.08;
    t.y += (t.ty - t.y) * 0.08;
    if (Math.abs(t.tx - t.x) > 0.002 || Math.abs(t.ty - t.y) > 0.002) busy = true;

    this.#apply();
    if (busy) this.#kick();
  }

  #apply() {
    this.ring.style.transform = `rotateY(${this.#rot.toFixed(2)}deg)`;
    this.style.setProperty('--tx', this.#tilt.x.toFixed(3));
    this.style.setProperty('--ty', this.#tilt.y.toFixed(3));
    const index = this.#indexAt(this.#rot);
    if (index === this.#active) return;
    this.#active = index;
    this.cards.forEach((card, i) => card.classList.toggle('is-active', i === index));
    const card = this.cards[index];
    const { name, status, link, tabs, tabButtons } = this.caption;
    tabButtons.forEach((tab, i) => tab.setAttribute('aria-current', i === index ? 'true' : 'false'));
    const activeTab = tabButtons[index];
    if (tabs && activeTab && tabs.scrollWidth > tabs.clientWidth) {
      tabs.scrollTo({ left: activeTab.parentElement.offsetLeft - (tabs.clientWidth - activeTab.offsetWidth) / 2, behavior: this.reduce ? 'auto' : 'smooth' });
    }
    if (name) name.textContent = card.dataset.name || '';
    if (status) { status.textContent = card.dataset.status || ''; status.hidden = Boolean(card.dataset.url); }
    if (link) {
      if (card.dataset.url) { link.href = card.dataset.url; link.hidden = false; }
      else link.hidden = true;
    }
  }
}

if (!customElements.get('s-ring-stage')) customElements.define('s-ring-stage', SRingStage);
